<template>
  <div class="exchange-management">
    <h2>换货/补发管理</h2>

    <!-- 搜索栏 -->
    <el-form inline class="search-bar">
      <el-form-item label="状态">
        <el-select v-model="searchStatus" clearable placeholder="全部状态" style="width:150px">
          <el-option label="待审核" :value="0" />
          <el-option label="已批准" :value="1" />
          <el-option label="已拒绝" :value="2" />
          <el-option label="处理中" :value="3" />
          <el-option label="已完成" :value="4" />
        </el-select>
      </el-form-item>
      <el-button type="primary" @click="loadExchanges">查询</el-button>
    </el-form>

    <!-- 申请列表 -->
    <el-table :data="exchangeList" border stripe v-loading="loading">
      <el-table-column prop="exchangeNo" label="换货单号" width="180" />
      <el-table-column prop="orderNo" label="原订单号" width="170" />
      <el-table-column label="类型" width="90">
        <template #default="{ row }">{{ exchangeTypeText(row.exchangeType) }}</template>
      </el-table-column>
      <el-table-column prop="reason" label="申请原因" min-width="150" show-overflow-tooltip />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status)">{{ statusText(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createdTime" label="申请时间" width="165" />
      <el-table-column label="操作" width="230" fixed="right">
        <template #default="{ row }">
          <el-button v-if="row.status === 0" size="small" @click="openApproveDialog(row)">审批</el-button>
          <el-button v-if="row.status === 1 || row.status === 3" size="small" type="primary"
                     @click="openShipmentDialog(row)">创建发货</el-button>
          <el-button v-if="row.status === 3" size="small" type="success"
                     @click="completeExchange(row.id)">完成</el-button>
          <el-button size="small" @click="showDetail(row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination">
      <el-pagination background @current-change="handlePageChange"
        :current-page="page" :page-size="size" :total="total"
        layout="total, prev, pager, next" />
    </div>

    <!-- 审批弹窗 -->
    <el-dialog title="审批换货申请" v-model="approveVisible" width="560px">
      <el-form label-width="100px">
        <el-form-item label="换货单号">
          <span>{{ currentExchange.exchangeNo }}</span>
        </el-form-item>
        <el-form-item label="申请类型">
          <el-tag :type="currentExchange.exchangeType === 1 ? 'warning' : 'primary'">
            {{ exchangeTypeText(currentExchange.exchangeType) }}
          </el-tag>
        </el-form-item>
        <el-form-item label="申请原因">
          <div>{{ currentExchange.reason }}</div>
        </el-form-item>
        <el-form-item label="详细说明" v-if="currentExchange.description">
          <div>{{ currentExchange.description }}</div>
        </el-form-item>
        <el-form-item label="换货商品" v-if="currentExchange.items && currentExchange.items.length">
          <div v-for="item in currentExchange.items" :key="item.productName + item.exchangeQuantity">
            {{ item.productName }} × {{ item.exchangeQuantity }}
          </div>
        </el-form-item>
        <el-form-item label="审批意见">
          <el-input v-model="approveForm.approveReason" type="textarea" placeholder="审批意见（拒绝时必填）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="approveVisible = false">取消</el-button>
        <el-button type="danger" @click="handleApprove(false)">拒绝</el-button>
        <el-button type="primary" @click="handleApprove(true)">批准</el-button>
      </template>
    </el-dialog>

    <!-- 发货弹窗 -->
    <el-dialog title="创建换货发货" v-model="shipmentVisible" width="500px">
      <el-form label-width="100px">
        <el-form-item label="运单号" required>
          <el-input v-model="shipmentForm.trackNo" placeholder="输入运单号" />
        </el-form-item>
        <el-form-item label="发货备注">
          <el-input v-model="shipmentForm.remark" type="textarea" placeholder="填写发货备注（选填）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="shipmentVisible = false">取消</el-button>
        <el-button type="primary" @click="handleCreateShipment">确认发货</el-button>
      </template>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog title="换货详情" v-model="detailVisible" width="700px">
      <el-descriptions :column="2" border v-if="currentExchange && currentExchange.exchangeNo">
        <el-descriptions-item label="换货单号">{{ currentExchange.exchangeNo }}</el-descriptions-item>
        <el-descriptions-item label="原订单号">{{ currentExchange.orderNo }}</el-descriptions-item>
        <el-descriptions-item label="申请类型">{{ exchangeTypeText(currentExchange.exchangeType) }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ statusText(currentExchange.status) }}</el-descriptions-item>
        <el-descriptions-item label="申请原因" :span="2">{{ currentExchange.reason }}</el-descriptions-item>
        <el-descriptions-item label="详细说明" :span="2" v-if="currentExchange.description">{{ currentExchange.description }}</el-descriptions-item>
        <el-descriptions-item label="审批人">{{ currentExchange.approverName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="审批时间">{{ currentExchange.approvedTime || '-' }}</el-descriptions-item>
        <el-descriptions-item label="审批意见" :span="2" v-if="currentExchange.approveReason">{{ currentExchange.approveReason }}</el-descriptions-item>
      </el-descriptions>

      <h4 style="margin:16px 0" v-if="currentExchange.items && currentExchange.items.length">换货商品</h4>
      <el-table :data="currentExchange.items || []" border size="small">
        <el-table-column prop="productName" label="商品" />
        <el-table-column prop="originalQuantity" label="原数量" width="90" />
        <el-table-column prop="exchangeQuantity" label="换货数量" width="100" />
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import {
  listExchanges,
  approveExchange as approveExchangeApi,
  createExchangeShipment,
  completeExchange as completeExchangeApi,
} from '@/api/mall';
import { ElMessageBox } from 'element-plus';

export default {
  name: 'ExchangeManagement',
  data() {
    return {
      searchStatus: null,
      exchangeList: [],
      loading: false,
      page: 1, size: 20, total: 0,
      approveVisible: false,
      shipmentVisible: false,
      detailVisible: false,
      currentExchange: {},
      approveForm: { approveReason: '' },
      shipmentForm: { trackNo: '', remark: '' },
      currentExchangeId: null,
    };
  },
  mounted() {
    this.loadExchanges();
  },
  methods: {
    handlePageChange(page) {
      this.page = page;
      this.loadExchanges();
    },
    async loadExchanges() {
      this.loading = true;
      try {
        const res = await listExchanges({
          status: this.searchStatus ?? undefined,
          page: this.page,
          size: this.size,
        });
        const pageData = res.data || res;
        this.exchangeList = pageData.records || [];
        this.total = pageData.total || 0;
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    openApproveDialog(row) {
      this.currentExchange = row;
      this.approveForm = { approveReason: '' };
      this.approveVisible = true;
    },
    async handleApprove(approved) {
      if (!approved && !this.approveForm.approveReason) {
        this.$message.warning('拒绝时必须填写审批意见');
        return;
      }
      try {
        await approveExchangeApi({
          exchangeId: this.currentExchange.id,
          approved,
          approveReason: this.approveForm.approveReason,
        });
        this.$message.success(approved ? '已批准' : '已拒绝');
        this.approveVisible = false;
        this.loadExchanges();
      } catch (e) { this.$message.error(e?.message || '操作失败'); }
    },
    openShipmentDialog(row) {
      this.currentExchange = row;
      this.currentExchangeId = row.id;
      this.shipmentForm = { trackNo: '', remark: '' };
      this.shipmentVisible = true;
    },
    async handleCreateShipment() {
      if (!this.shipmentForm.trackNo) {
        this.$message.warning('请输入运单号');
        return;
      }
      try {
        await createExchangeShipment(this.currentExchangeId, {
          orderNo: this.currentExchange.orderNo,
          trackNo: this.shipmentForm.trackNo,
          remark: this.shipmentForm.remark,
        });
        this.$message.success('发货成功');
        this.shipmentVisible = false;
        this.loadExchanges();
      } catch (e) { this.$message.error(e?.message || '发货失败'); }
    },
    async completeExchange(exchangeId) {
      try {
        await ElMessageBox.confirm('确定完成该换货吗？', '确认', { type: 'warning' });
        await completeExchangeApi(exchangeId);
        this.$message.success('已完成');
        this.loadExchanges();
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') this.$message.error(e?.message || '操作失败');
      }
    },
    showDetail(row) {
      this.currentExchange = row;
      this.detailVisible = true;
    },
    exchangeTypeText(type) {
      const map = { 1: '补发', 2: '换货', 3: '退货' };
      return map[type] || '';
    },
    statusText(status) {
      const map = { 0: '待审核', 1: '已批准', 2: '已拒绝', 3: '处理中', 4: '已完成', 5: '已取消' };
      return map[status] || '';
    },
    statusTag(status) {
      const map = { 0: 'warning', 1: 'success', 2: 'info', 3: 'primary', 4: 'success', 5: 'info' };
      return map[status] || '';
    },
  },
};
</script>

<style scoped>
.exchange-management { padding: 0; }
.search-bar { background: #fff; padding: 16px; border-radius: 4px; margin-bottom: 16px; }
.pagination { margin-top: 16px; display: flex; justify-content: flex-end; }
</style>
